import React, { useEffect, useState } from "react";
import { Title } from "../../../stories/Title/Title";
import { ButtonRadioSelect } from "../../../stories/ButtonRadioSelect/ButtonRadioSelect";
import { Select } from "@mantine/core";
import {
  getGingivaShadeFormChoices,
  getShadeTypeFormChoices,
  getArchFromTeeth,
} from "../../../utils/product.utils";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { filterShadeByShadeType } from "../../../utils/item.utils";
import { FixedProsthesisShadeType, WorkflowType } from "../../../types/enums";
import { RemovableItem } from "../../../types/interfaces";

export const MaterialAndShadeComponent = ({
  control,
  errors,
  removableItemChoicesData,
  watch,
  setValue,
  number,
  toggleMultiItem,
  removableItem,
  workflowType,
  removableItemLength,
}: {
  control: Control<any>;
  errors: FieldErrors<any>;
  removableItemChoicesData: any;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  number: number;
  toggleMultiItem: boolean;
  removableItem?: RemovableItem;
  workflowType?: WorkflowType;
  removableItemLength?: number;
}) => {
  const [selectedShadeList, setSelectedShadeList] = useState<
    { value: string; label: string }[]
  >([]);
  const selectedShadeBrand = watch(`brand${number}`);
  const selectedShade = watch(`shade${number}`);
  const selectedGingivaShade = watch(`gingivaShade${number}`);
  const selectedMaterial = watch(`material${number}`);

  useEffect(() => {
    const list = filterShadeByShadeType(
      removableItemChoicesData?.getItemShadesWhere ?? [],
      selectedShadeBrand as FixedProsthesisShadeType
    );
    setSelectedShadeList(list);
    if (removableItemLength > 1) {
      setValue(`brand1`, selectedShadeBrand);
      setValue(`shade1`, selectedShade);
      setValue(`gingivaShade1`, selectedGingivaShade);
      setValue(`material1`, selectedMaterial);
    }
    if (!list.some(shade => shade.value === selectedShade)) {
      setValue(`shade${number}`, null);
    }
  }, [
    selectedShadeBrand,
    selectedShade,
    selectedGingivaShade,
    selectedMaterial,
    removableItemChoicesData?.getItemShadesWhere,
  ]);

  return (
    <>
      {toggleMultiItem && (
        <Title
          title={`Caractéristiques de la ${getArchFromTeeth(removableItem?.teeth ?? [])}`}
          size={4}
          mb="md"
          mt="lg"
        />
      )}
      <Controller
        control={control}
        name={`material${number}`}
        rules={{ required: "Veuillez sélectionner un matériau" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={(
              removableItemChoicesData?.getItemMaterialsWhere ?? []
            ).map((item: any) => ({
              label: item.label,
              value: item.id,
            }))}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Matériau"
            dataTestId="material"
          />
        )}
      />
      {errors[`material${number}`] && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors[`material${number}`].message as string}
        </span>
      )}
      {workflowType !== WorkflowType.WAX_RIM && (
        <>
          <Controller
            control={control}
            name={`brand${number}`}
            rules={{ required: "Veuillez sélectionner une marque de teintier" }}
            render={({ field }) => (
              <ButtonRadioSelect
                options={getShadeTypeFormChoices(removableItemChoicesData)}
                setSelected={value => field.onChange(value as string)}
                selected={field.value}
                label="Type de teintier"
                dataTestId="brand"
              />
            )}
          />
          {errors[`brand${number}`] && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors[`brand${number}`].message as string}
            </span>
          )}
          <Title title="Teinte des dents" size={5} />
          <Controller
            control={control}
            name={`shade${number}`}
            rules={{ required: "Veuillez sélectionner une teinte" }}
            render={({ field }) => (
              <Select
                ref={field.ref}
                my="xs"
                data-testid="select-teinte"
                searchable
                clearable
                placeholder="Selectionnez une teinte"
                data={selectedShadeList}
                onChange={value => field.onChange(value as string)}
                value={field.value}
              />
            )}
          />
          {errors[`shade${number}`] && (
            <span
              data-testid="error-shade"
              style={{ color: "red", fontSize: "14px" }}
            >
              {errors[`shade${number}`].message as string}
            </span>
          )}
          <Controller
            control={control}
            name={`gingivaShade${number}`}
            rules={{ required: "Veuillez sélectionner une teinte de gencive" }}
            render={({ field }) => (
              <ButtonRadioSelect
                options={getGingivaShadeFormChoices(removableItemChoicesData)}
                setSelected={value => field.onChange(value as string)}
                selected={field.value}
                label="Teinte de gencive"
                dataTestId="gingiva-shade"
              />
            )}
          />
          {errors[`gingivaShade${number}`] && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors[`gingivaShade${number}`].message as string}
            </span>
          )}
        </>
      )}
    </>
  );
};
